/**
 * useAccountsPayable - v2.0 URL-Synced
 * 
 * Hook refatorado para usar paginação vinda dos props (não useState interno).
 * A sincronização com URL é feita pelo hook pai (useFinancialFilters).
 * 
 * Última atualização: 2026-01-28
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AccountPayable } from '@/types/financial';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { useStableAuth } from '@/hooks/useStableAuth';
import { auditService } from '@/services/auditService';
import { assertPaymentMethodSlug } from '@/utils/paymentMethodGuard';
import { translateSupabaseError } from '@/utils/errorTranslation';
import { formatCurrency } from '@/utils/formatters';
import { addDays, format } from 'date-fns';
import { splitIntoInstallments } from '@/hooks/useSaleInstallments';

export interface RenegotiatePayableData {
  payableId: string;
  numberOfInstallments: number;
  firstDueDate: string;
  intervalDays: number;
}

interface PayablesFilteredResponse {
  data: any[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface AccountsPayableFilters {
  search?: string;
  status?: string;
  supplierId?: string;
  categoryId?: string;
  startDate?: string;
  endDate?: string;
  dateField?: 'due_date' | 'payment_date';
  // Pagination - now comes from external source (URL)
  page?: number;
  pageSize?: number;
}

/** R-COMPRAS.20: jsonb devolvido por register_manual_payable_payment (6 chaves). */
export interface RegisterPayablePaymentResult {
  payment_id: string;
  payable_id: string;
  financial_account_id: string;
  principal: number;
  paid_value_after: number;
  status_after: 'paid' | 'partial';
}

const PAYABLE_QUERY_KEYS = [
  'accounts-payable', 'payables-stats', 'financial-dashboard',
  'financial-accounts', 'expenses',
];

export const useAccountsPayable = (filters: AccountsPayableFilters = {}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId, isContextReady } = useMultiTenant();
  const { userId } = useStableAuth();

  const invalidateAll = (extras?: string[]) => {
    PAYABLE_QUERY_KEYS.forEach(key =>
      queryClient.invalidateQueries({ queryKey: [key] })
    );
    extras?.forEach(key =>
      queryClient.invalidateQueries({ queryKey: [key] })
    );
  };

  // Use pagination from filters (default to page 1 if not provided)
  // The parent component is responsible for URL sync via useFinancialFilters
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 50;

  const query = useQuery({
    queryKey: ['accounts-payable', companyId, filters],
    queryFn: async () => {
      // Ensure page is at least 1 (1-based for RPC)
      const safePage = Math.max(1, page);
      
      // D-P8.11: ordenação decidida pelo período selecionado
      const today = format(new Date(), 'yyyy-MM-dd');
      const startDate = filters?.startDate;
      const endDate = filters?.endDate;
      const hasPeriod = !!startDate && !!endDate && startDate !== 'all' && endDate !== 'all';
      const isCurrentPeriod = hasPeriod && startDate <= today && today <= endDate;

      const params = {
        p_company_id: companyId,
        p_search: filters?.search?.trim() || null,
        p_status: filters?.status && filters.status !== 'all' ? filters.status : null,
        p_supplier_id: filters?.supplierId || null,
        p_category_id: filters?.categoryId || null,
        p_start_date: filters?.startDate && filters.startDate !== 'all' ? filters.startDate : null,
        p_end_date: filters?.endDate && filters.endDate !== 'all' ? filters.endDate : null,
        p_page: safePage,
        p_page_size: pageSize,
        p_date_field: filters?.dateField === 'payment_date' ? 'payment_date' : 'due_date',
        ...(hasPeriod
          ? isCurrentPeriod
            ? { p_sort_field: 'created_at', p_sort_dir: 'desc' }
            : { p_sort_field: filters?.dateField === 'payment_date' ? 'payment_date' : 'due_date', p_sort_dir: 'asc' }
          : {}),
      };

      const { data, error } = await supabase.rpc('get_payables_filtered', params);

      if (error) {
        console.error('[useAccountsPayable] ❌ RPC Error:', error);
        throw error;
      }
      
      // BLINDAGEM: Handle different response formats
      if (!data) {
        console.warn('[useAccountsPayable] ⚠️ No data returned');
        return { data: [], total_count: 0, page: 1, page_size: 50, total_pages: 0 };
      }
      
      // Defensive parsing - handle both direct array and wrapped responses
      const rawData = data as any;
      const resultData = Array.isArray(rawData) ? rawData : (rawData?.data || []);
      const totalCount = typeof rawData?.total_count === 'number' ? rawData.total_count : resultData.length;
      
      
      return {
        data: resultData,
        total_count: totalCount,
        page: rawData?.page || 1,
        page_size: rawData?.page_size || 50,
        total_pages: rawData?.total_pages || Math.ceil(totalCount / 50)
      };
    },
    enabled: !!companyId && isContextReady,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const statsQuery = useQuery({
    queryKey: ['payables-stats', companyId, filters?.startDate, filters?.endDate, filters?.dateField, filters?.supplierId, filters?.categoryId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_payables_stats', {
        p_company_id: companyId,
        p_start_date: filters?.startDate && filters.startDate !== 'all' ? filters.startDate : null,
        p_end_date: filters?.endDate && filters.endDate !== 'all' ? filters.endDate : null,
        p_supplier_id: filters?.supplierId || null,
        p_category_id: filters?.categoryId || null,
        p_date_field: filters?.dateField === 'payment_date' ? 'payment_date' : 'due_date',
      });
      if (error) throw error;
      return data?.[0] || {};
    },
    enabled: !!companyId && isContextReady,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: async (payable: Partial<AccountPayable> & { is_paid?: boolean; is_scheduled?: boolean }) => {
      if (!companyId) {
        throw new Error('Company ID não disponível. Faça login novamente.');
      }
      assertPaymentMethodSlug(payable.payment_method);

      // Extrair campos que não existem no banco e mapear para campos corretos
      const { is_paid, is_scheduled, ...cleanPayable } = payable as any;
      const wantsPaid = Boolean(is_paid) || cleanPayable.status === 'paid';
      if (wantsPaid && !cleanPayable.financial_account_id) throw new Error('Conta financeira obrigatória para conta já paga');

      const { data: numberData } = await supabase.rpc('generate_next_payable_number');

      // R-P8.5: título nasce pending; a baixa é da RPC (dona única)
      const mappedPayable = {
        ...cleanPayable,
        company_id: companyId,
        transaction_number: numberData,
        status: 'pending',
        payment_date: null,
        paid_value: 0,
      };

      const { data, error } = await supabase
        .from('accounts_payable')
        .insert([mappedPayable])
        .select()
        .single();

      if (error) throw error;

      if (wantsPaid) {
        const paymentDate = cleanPayable.payment_date || data.due_date || new Date().toISOString().split('T')[0];
        const { data: rpc, error: rpcError } = await supabase.rpc('register_manual_payable_payment', {
          p_payable_id: data.id,
          p_payment_value: Number(data.total_value) - Number(data.discount_value || 0),
          p_payment_date: paymentDate,
          p_payment_method: data.payment_method || 'pix',
          p_financial_account_id: data.financial_account_id,
          p_notes: 'Pagamento registrado automaticamente na criação',
        });
        if (rpcError) throw rpcError;
        const result = rpc as { status_after: string; paid_value_after: number };
        return {
          ...data,
          status: result.status_after,
          paid_value: result.paid_value_after,
          payment_date: paymentDate,
        } as AccountPayable;
      }

      return data as AccountPayable;
    },
    onSuccess: async (data) => {
      invalidateAll();
      
      await auditService.logAction({
        action: 'CRIAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_pagar',
        entityId: data.id,
        description: `Conta a pagar criada: ${data.transaction_number}`,
        details: {
          transaction_number: data.transaction_number,
          supplier_id: data.supplier_id,
          total_value: data.total_value,
          due_date: data.due_date,
          description: data.description
        }
      });
      
      toast({ title: 'Conta a pagar criada com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar conta a pagar',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<AccountPayable> & { id: string }) => {
      assertPaymentMethodSlug(updates.payment_method);
      const { data, error } = await supabase
        .from('accounts_payable')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (updatedData) => {
      invalidateAll(['purchases']);
      
      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_pagar',
        entityId: updatedData.id,
        description: `Conta a pagar atualizada: ${updatedData.transaction_number}`,
        details: {
          transaction_number: updatedData.transaction_number,
          supplier_id: updatedData.supplier_id,
          total_value: updatedData.total_value,
          status: updatedData.status
        }
      });
      
      toast({ title: 'Conta a pagar atualizada com sucesso' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar conta a pagar',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Use RPC function for cascading delete with proper permissions
      const { data, error } = await supabase.rpc('soft_delete_payable', {
        p_payable_id: id
      });
      
      if (error) throw error;
      return data as { success: boolean; transaction_number: string; total_value: number; cascade_deleted_purchase: boolean };
    },
    onSuccess: async (result) => {
      invalidateAll(['purchases', 'purchases-stats']);
      
      const message = result?.cascade_deleted_purchase 
        ? 'Conta a pagar e compra vinculada excluídas com sucesso'
        : 'Conta a pagar excluída com sucesso';
      
      toast({ title: message });
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao excluir conta a pagar',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // ====================================
  // RENEGOTIATE BALANCE MUTATION
  // ====================================
  const renegotiatePayableMutation = useMutation({
    mutationFn: async (data: RenegotiatePayableData) => {
      if (!companyId) {
        throw new Error('Company ID não disponível');
      }

      // 1. Buscar o payable atual
      const { data: currentPayable, error: fetchError } = await supabase
        .from('accounts_payable')
        .select('*')
        .eq('id', data.payableId)
        .single();

      if (fetchError || !currentPayable) {
        throw new Error('Conta a pagar não encontrada');
      }

      // 2. Calcular saldo devedor
      const balance = Math.max(0, currentPayable.total_value - (currentPayable.paid_value || 0));
      if (balance <= 0) {
        throw new Error('Não há saldo restante para renegociar');
      }

      // 3. Dividir saldo em N parcelas
      const installmentValues = splitIntoInstallments(balance, data.numberOfInstallments);

      // 4. Criar novas parcelas
      const newPayables = [];
      for (let i = 0; i < data.numberOfInstallments; i++) {
        const dueDate = addDays(new Date(data.firstDueDate), i * data.intervalDays);
        
        // Gerar novo número de transação
        const { data: numberData } = await supabase.rpc('generate_next_payable_number');
        
        newPayables.push({
          company_id: companyId,
          transaction_number: numberData,
          supplier_id: currentPayable.supplier_id,
          expense_category_id: currentPayable.expense_category_id,
          cost_center_id: currentPayable.cost_center_id,
          financial_account_id: currentPayable.financial_account_id,
          description: `${currentPayable.description} (Renegociação ${i + 1}/${data.numberOfInstallments})`,
          issue_date: format(new Date(), 'yyyy-MM-dd'),
          due_date: format(dueDate, 'yyyy-MM-dd'),
          original_value: installmentValues[i],
          total_value: installmentValues[i],
          paid_value: 0,
          status: 'pending',
          payment_method: currentPayable.payment_method,
          repeat_transaction: false,
          installment_number: i + 1,
          total_installments: data.numberOfInstallments,
          parent_transaction_id: currentPayable.id,
          origin_type: currentPayable.origin_type,
          // comissão: origin_number NÃO propaga — colide com uq_accounts_payable_commission_origin (23505);
          // rastreabilidade via parent_transaction_id + notes
          origin_number: currentPayable.origin_type === 'commission' ? null : currentPayable.origin_number,
          notes: `Originado da renegociação de ${currentPayable.transaction_number}`,
          // Forense UUID (SI.140-C-iii): lote nunca nasce órfão de autor
          created_by: userId,
        });
      }

      // 5. Inserir novas parcelas
      const { error: insertError } = await supabase
        .from('accounts_payable')
        .insert(newPayables);

      if (insertError) {
        console.error('[renegotiatePayable] Insert error:', insertError);
        throw insertError;
      }

      // 6. Atualizar payable original com status renegotiated e nota de auditoria
      const auditNote = `[${format(new Date(), 'dd/MM/yyyy HH:mm')}] Saldo de ${formatCurrency(balance)} renegociado em ${data.numberOfInstallments} parcelas.`;
      const updatedNotes = currentPayable.notes 
        ? `${currentPayable.notes}\n\n${auditNote}` 
        : auditNote;

      const { error: updateError } = await supabase
        .from('accounts_payable')
        .update({
          status: 'renegotiated',
          notes: updatedNotes,
        })
        .eq('id', data.payableId);

      if (updateError) {
        console.error('[renegotiatePayable] Update error:', updateError);
        throw updateError;
      }

      return { 
        originalPayable: currentPayable, 
        newInstallmentsCount: data.numberOfInstallments,
        totalRenegotiated: balance,
      };
    },
    onSuccess: async (result) => {
      invalidateAll();

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_pagar',
        entityId: result.originalPayable.id,
        description: `Saldo renegociado: ${result.originalPayable.transaction_number} (${result.newInstallmentsCount} novas parcelas)`,
        details: {
          action_subtype: 'renegociacao',
          transaction_number: result.originalPayable.transaction_number,
          original_total: result.originalPayable.total_value,
          renegotiated_balance: result.totalRenegotiated,
          new_installments: result.newInstallmentsCount,
        }
      });

      toast({
        title: 'Saldo renegociado com sucesso',
        description: `${result.newInstallmentsCount} novas parcelas criadas.`,
      });
    },
    onError: (error: any) => {
      console.error('[renegotiatePayable] Error:', error);
      toast({
        title: 'Erro ao renegociar saldo',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  const reopenPayableMutation = useMutation({
    mutationFn: async ({ id: payableId, reason }: { id: string; reason: string }) => {
      // Buscar payable atual para auditoria
      const { data: payable, error: fetchError } = await supabase
        .from('accounts_payable')
        .select('*')
        .eq('id', payableId)
        .single();

      if (fetchError) throw fetchError;
      if (!payable) throw new Error('Lançamento não encontrado');

      // Chamar RPC que trata conciliação bancária — ESTORNO-AUDITORIA (I.160, E3): motivo obrigatório
      const { data: result, error: rpcError } = await supabase.rpc('revert_payable_payment', {
        p_payable_id: payableId,
        p_reason: reason,
      });

      if (rpcError) throw rpcError;

      return { ...payable, revertResult: result };
    },
    onSuccess: async (payable) => {
      invalidateAll(['purchases', 'bank-transactions']);

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_pagar',
        entityId: payable.id,
        description: `Conta reaberta (estorno): ${payable.transaction_number}`,
        details: {
          transaction_number: payable.transaction_number,
          previous_status: payable.status,
          previous_paid_value: payable.paid_value,
        }
      });

      toast({ title: 'Pagamentos estornados' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao estornar',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // ====================================
  // REVERT PAYMENT ENTRY (P8.4)
  // ====================================
  const revertPayablePaymentEntryMutation = useMutation({
    // ESTORNO-AUDITORIA (I.160, E3): motivo obrigatório
    mutationFn: async ({ id: paymentId, reason }: { id: string; reason: string }) => {
      const { data: result, error: rpcError } = await supabase.rpc('revert_payable_payment_entry', {
        p_payment_id: paymentId,
        p_reason: reason,
      });

      if (rpcError) throw rpcError;
      return result as { payment_id: string; payable_id: string; principal_reverted: number };
    },
    onSuccess: async (result) => {
      invalidateAll(['purchases', 'bank-transactions', 'payable-payments', 'payable-detail', 'payment-reversals']);

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_pagar',
        entityId: result.payable_id,
        description: `Lançamento estornado: ${result.principal_reverted}`,
        details: result,
      });

      toast({ title: 'Lançamento estornado' });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao estornar lançamento',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // ====================================
  // RECORD PAYMENT MUTATION
  // ====================================
  const recordPaymentMutation = useMutation({
    mutationFn: async (params: {
      payableId: string;
      paymentDate: string;
      paymentValue: number;
      paymentMethod: string;
      financialAccountId: string;
      interestValue: number;
      fineValue: number;
      discountValue: number;
      feeValue: number;
      notes?: string;
      /** R-COMPRAS.20: sucesso contextual (a tela mostra o próprio resumo) — pula SÓ o toast */
      silentSuccess?: boolean;
    }) => {
      assertPaymentMethodSlug(params.paymentMethod);

      // P8.2 (§I.147-quater): RPC dona única da baixa — valida, insere o payment
      // e recompõe o título numa transação
      const { data: rpc, error: rpcError } = await supabase.rpc('register_manual_payable_payment', {
        p_payable_id: params.payableId,
        p_payment_value: params.paymentValue,
        p_payment_date: params.paymentDate,
        p_payment_method: params.paymentMethod,
        p_financial_account_id: params.financialAccountId ?? undefined,
        p_interest_value: params.interestValue,
        p_fine_value: params.fineValue,
        p_discount_value: params.discountValue,
        p_fee_value: params.feeValue,
        p_notes: params.notes ?? undefined,
      });
      if (rpcError) throw rpcError;
      // R-COMPRAS.20: o retorno inteiro segue adiante (aditivo — payableId/newStatus/paymentId
      // mantidos para os callers existentes); antes 3 das 6 chaves morriam aqui
      const result = rpc as unknown as RegisterPayablePaymentResult;

      return {
        payableId: params.payableId,
        newStatus: result.status_after,
        paymentId: result.payment_id,
        principal: result.principal,
        paidValueAfter: result.paid_value_after,
        financialAccountId: result.financial_account_id,
      };
    },
    onSuccess: async (result, variables) => {
      invalidateAll(['payable-detail', 'bank-transactions', 'payable-payments']);

      await auditService.logAction({
        action: 'ATUALIZAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'conta_pagar',
        entityId: result.payableId,
        description: `Pagamento registrado para conta a pagar`,
        details: { new_status: result.newStatus },
      });

      if (!variables.silentSuccess) {
        toast({ title: 'Pagamento registrado com sucesso!' });
      }
    },
    onError: (error) => {
      toast({
        title: 'Erro ao registrar pagamento',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // ====================================
  // CREATE PAYABLE GROUP MUTATION
  // ====================================
  const createPayableGroupMutation = useMutation({
    mutationFn: async (groupData: {
      description: string;
      total_value: number;
      installments_count: number;
      group_type: 'installment' | 'recurrence';
      source_type: string;
      payment_method?: string;
      issue_date?: string;
      first_due_date: string;
      interval_days?: number;
      interval_months?: number;
      supplier_id?: string;
      expense_category_id?: string;
      cost_center_id?: string;
      financial_account_id?: string;
      invoice_number?: string;
      notes?: string;
      recurrence_frequency?: string;
      recurrence_occurrences?: number;
    }) => {
      if (!companyId) {
        throw new Error('Company ID não disponível. Faça login novamente.');
      }
      assertPaymentMethodSlug(groupData.payment_method);

      // R-P8.19 (E3): sem intervalo nenhum, lança — sem fallback silencioso.
      // interval_days em grupo mensal é redundância inerte (30×meses, round-trip
      // da edição); o cálculo de datas usa interval_months na RPC.
      let effectiveIntervalDays: number;
      if (groupData.interval_days != null) {
        effectiveIntervalDays = groupData.interval_days;
      } else if (groupData.interval_months != null) {
        effectiveIntervalDays = groupData.interval_months * 30;
      } else {
        throw new Error('Intervalo do grupo ausente');
      }

      let recurrenceTemplateId: string | null = null;

      // 1. Se recorrência, criar template primeiro
      if (groupData.group_type === 'recurrence' && groupData.recurrence_frequency) {
        const templateIntervalDays = effectiveIntervalDays;

        const { data: template, error: templateError } = await supabase
          .from('recurrence_templates')
          .insert({
            company_id: companyId,
            frequency: groupData.recurrence_frequency,
            interval_days: templateIntervalDays,
            start_date: groupData.first_due_date,
            next_run_date: groupData.first_due_date,
            active: true,
          })
          .select()
          .single();

        if (templateError) throw templateError;
        recurrenceTemplateId = template.id;
      }

      // 2. Criar transaction_group com metadados
      const { data: group, error: groupError } = await supabase
        .from('transaction_groups')
        .insert({
          company_id: companyId,
          group_type: groupData.group_type,
          source_type: groupData.source_type,
          description: groupData.description,
          total_value: groupData.total_value,
          installments_count: groupData.installments_count,
          payment_method: groupData.payment_method || null,
          recurrence_template_id: recurrenceTemplateId,
          supplier_id: groupData.supplier_id || null,
          expense_category_id: groupData.expense_category_id || null,
          cost_center_id: groupData.cost_center_id || null,
          financial_account_id: groupData.financial_account_id || null,
          invoice_number: groupData.invoice_number || null,
          notes: groupData.notes || null,
          issue_date: groupData.issue_date || format(new Date(), 'yyyy-MM-dd'),
          first_due_date: groupData.first_due_date,
          interval_days: effectiveIntervalDays,
          interval_months: groupData.interval_months ?? null,
        } as any)
        .select()
        .single();

      if (groupError) throw groupError;

      // 3. Gerar parcelas via RPC
      const { data: count, error: rpcError } = await supabase
        .rpc('generate_installments_from_group', { p_group_id: group.id });

      if (rpcError) throw rpcError;

      return { group, installmentsGenerated: count as number };
    },
    onSuccess: async (result) => {
      invalidateAll();

      await auditService.logAction({
        action: 'CRIAÇÃO',
        module: 'FINANCEIRO',
        entityType: 'transaction_group',
        entityId: result.group.id,
        description: `Grupo de despesas criado: ${result.group.description} (${result.installmentsGenerated} parcelas)`,
        details: {
          group_id: result.group.id,
          total_value: result.group.total_value,
          installments_count: result.group.installments_count,
          group_type: result.group.group_type,
        }
      });

      toast({
        title: `Grupo de despesas criado e ${result.installmentsGenerated} parcela(s) gerada(s) com sucesso`,
      });
    },
    onError: (error) => {
      console.error('[createPayableGroup] Error:', error);
      toast({
        title: 'Erro ao criar grupo de despesas',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // ====================================
  // UPDATE PAYABLE GROUP (REGENERATE)
  // ====================================
  const updatePayableGroupMutation = useMutation({
    mutationFn: async (params: {
      groupId: string;
      totalValue: number;
      installmentsCount: number;
      firstDueDate: string;
      intervalDays: number;
      description?: string;
      supplierId?: string;
      expenseCategoryId?: string;
      costCenterId?: string;
      financialAccountId?: string;
      invoiceNumber?: string;
      notes?: string;
      paymentMethod?: string;
      issueDate?: string;
    }) => {
      const { data, error } = await supabase.rpc('update_payable_group_and_regenerate', {
        p_group_id: params.groupId,
        p_total_value: params.totalValue,
        p_installments_count: params.installmentsCount,
        p_first_due_date: params.firstDueDate,
        p_interval_days: params.intervalDays,
        p_description: params.description || null,
        p_supplier_id: params.supplierId || null,
        p_expense_category_id: params.expenseCategoryId || null,
        p_cost_center_id: params.costCenterId || null,
        p_financial_account_id: params.financialAccountId || null,
        p_invoice_number: params.invoiceNumber || null,
        p_notes: params.notes || null,
        p_payment_method: params.paymentMethod || null,
        p_issue_date: params.issueDate || null,
      });
      if (error) throw error;
      const result = data as any;
      if (result && !result.success) {
        throw new Error(result.error || 'Erro ao regenerar grupo');
      }
      return result;
    },
    onSuccess: (result) => {
      invalidateAll();
      toast({
        title: `Grupo atualizado: ${result.deleted_count} parcelas removidas, ${result.generated_count} regeneradas`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar grupo',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // ====================================
  // UPDATE PAYABLE GROUP METADATA ONLY
  // ====================================
  const updatePayableGroupMetadataMutation = useMutation({
    mutationFn: async (params: {
      groupId: string;
      description?: string;
      supplierId?: string;
      expenseCategoryId?: string;
      costCenterId?: string;
      financialAccountId?: string;
      invoiceNumber?: string;
      notes?: string;
      paymentMethod?: string;
    }) => {
      const { data, error } = await supabase.rpc('update_payable_group_metadata', {
        p_group_id: params.groupId,
        p_description: params.description || null,
        p_supplier_id: params.supplierId || null,
        p_expense_category_id: params.expenseCategoryId || null,
        p_cost_center_id: params.costCenterId || null,
        p_financial_account_id: params.financialAccountId || null,
        p_invoice_number: params.invoiceNumber || null,
        p_notes: params.notes || null,
        p_payment_method: params.paymentMethod || null,
      });
      if (error) throw error;
      return data as any;
    },
    onSuccess: (result) => {
      invalidateAll();
      toast({
        title: `Dados do grupo atualizados (${result.updated_count} parcelas)`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar dados do grupo',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  // ====================================
  // DELETE PAYABLE GROUP MUTATION
  // ====================================
  const deletePayableGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      const { data, error } = await supabase.rpc('delete_payable_group', {
        p_group_id: groupId,
      });
      if (error) throw error;
      const result = data as any;
      if (result && !result.success) {
        throw new Error(result.error || 'Erro ao excluir grupo');
      }
      return result;
    },
    onSuccess: (result) => {
      invalidateAll();
      toast({
        title: `Grupo excluído: ${result.deleted_count} parcela(s) removida(s)`,
        description: result.preserved_count > 0 ? `${result.preserved_count} parcela(s) com pagamento preservada(s)` : undefined,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir grupo',
        description: translateSupabaseError(error),
        variant: 'destructive',
      });
    },
  });

  return {
    payables: query.data?.data || [],
    totalCount: query.data?.total_count || 0,
    totalPages: query.data?.total_pages || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading,
    currentPage: query.data?.page || 1,
    pageSize: query.data?.page_size || 50,
    createPayable: createMutation.mutate,
    createPayableAsync: createMutation.mutateAsync,
    createPayableGroup: createPayableGroupMutation.mutateAsync,
    updatePayable: updateMutation.mutate,
    updatePayableGroup: updatePayableGroupMutation.mutateAsync,
    updatePayableGroupMetadata: updatePayableGroupMetadataMutation.mutateAsync,
    deletePayable: deleteMutation.mutate,
    deletePayableGroup: deletePayableGroupMutation.mutateAsync,
    renegotiatePayable: renegotiatePayableMutation.mutate,
    reopenPayable: reopenPayableMutation.mutate,
    revertPayablePaymentEntry: revertPayablePaymentEntryMutation.mutate,
    recordPayment: recordPaymentMutation.mutate,
    recordPaymentAsync: recordPaymentMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isCreatingGroup: createPayableGroupMutation.isPending,
    isUpdating: updateMutation.isPending,
    isUpdatingGroup: updatePayableGroupMutation.isPending || updatePayableGroupMetadataMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isDeletingGroup: deletePayableGroupMutation.isPending,
    isRenegotiating: renegotiatePayableMutation.isPending,
    isReopening: reopenPayableMutation.isPending,
    isRevertingPayablePaymentEntry: revertPayablePaymentEntryMutation.isPending,
    isRecordingPayment: recordPaymentMutation.isPending,
    refetch: query.refetch,
  };
};
